// src/pages/notesManagement.tsx
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { PieCategoryChart } from "./PieCategoryChart";
import { useReceipt } from "../../hooks/useReceipt";
import { CameraModal } from "./photoCapture";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const CATEGORIES = [
  { id: "combustivel", label: "Combustível" },
  { id: "mercado", label: "Mercado" },
  { id: "contas", label: "Contas de consumo" },
  { id: "alimentacao", label: "Alimentação" },
  { id: "farmacia", label: "Farmácia" },
  { id: "outros", label: "Outros" }
];

export default function NotesManagement() {

  const formatCurrency = (value) =>
    Number(value).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });

  // UI state
  const [activeTab, setActiveTab] = useState("scan");
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [partners, setPartners] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [receiptsFiltered, setReceiptsFiltered] = useState([]);
  const [additionalInfo, setAdditionalInfo] = useState({
    issueDate: null,
    emitterCNPJ: null,
    emitterName: null,
    totalValue: null,
  });

  const navigate = useNavigate();

  const { listReceipts, createReceipt, scanReceipt, uploadReceipt, listPartners } = useReceipt();

  // Form
  const [category, setCategory] = useState(CATEGORIES[0].label);
  const [noteInput, setNoteInput] = useState("");
  const [btnDisabled, setBtnDisabled] = useState(true);
  const [cameraOpen, setCameraOpen] = useState(false);
  const fileRef = useRef(null);
  const [fileName, setFileName] = useState(null);
  const [capturedFile, setCapturedFile] = useState(null);
  const [isManualModalOpen, setIsManualModalOpen] = useState(false);
  const [missingFields, setMissingFields] = useState({});

  const showToast = (message, type) =>
    toast[type](message, {
      position: "top-right",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      theme: "colored",
    });

  useEffect(() => {
    const allFilled = Object.values(additionalInfo).every(
      (value) => value !== null && value !== "" && value !== undefined
    );

    setBtnDisabled(!allFilled);
  }, [additionalInfo]);

  const [activeFilter, setActiveFilter] = useState("all");

  const [redeemOpen, setRedeemOpen] = useState(false);

  useEffect(() => {
    async function fetchListReceipts() {
      if (isFirstLoad) {
        const responseReceipts = await listReceipts();
        const responsePartners = await listPartners();

        setPartners(responsePartners);
        setReceipts(responseReceipts);
        setReceiptsFiltered(responseReceipts);
        setIsFirstLoad(false);
      }
    }
    fetchListReceipts();
  }, []);

  async function handleReceiptsByCategory(category) {

    if (category === "all") {
      setReceiptsFiltered(receipts);
      setActiveFilter("all");
      return;
    }

    const filtered = receipts.filter((receipt) => receipt.category === category);
    setActiveFilter(category);
    setReceiptsFiltered(filtered);
  }

  function groupByCategory(receipts) {
    const grouped = receipts.reduce((acc, item) => {
      const category = item.category || 'Sem categoria';
      const value = Number(item.totalValue) || 0;

      if (!acc[category]) {
        acc[category] = 0;
      }

      acc[category] += value;
      return acc;
    }, {});

    return Object.entries(grouped).map(([category, total]) => ({
      category,
      total
    }));
  }

  function translateStatus(status) {
    const map = {
      pending: "Pendente",
      valid: "Válido",
      invalid: "Inválido",
      expired: "Expirado",
      fraud_suspect: "Suspeita de Fraude",
    };

    return map[status] ?? "Status Desconhecido";
  }

  async function handleOpenCamera() {
    try {
      // 0. Verificar se o navegador suporta mediaDevices
      if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
        alert("Seu navegador não suporta acesso à câmera.");
        return;
      }

      // 1. Verificar se existem câmeras no dispositivo
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoInputs = devices.filter((d) => d.kind === "videoinput");

      if (videoInputs.length === 0) {
        alert("Nenhuma câmera foi encontrada no dispositivo.");
        return;
      }

      // 2. Verificar estado da permissão ANTES de pedir acesso
      const permission = await navigator.permissions.query({
        name: "camera",
      });

      // 3. Se estiver "denied", o navegador NÃO mostra popup
      if (permission.state === "denied") {
        alert(
          "A permissão da câmera está bloqueada. Ative manualmente nas configurações do navegador."
        );
        return;
      }

      // 4. Chamar o popup de permissão
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });

      // Sucesso → pode abrir o modal
      stream.getTracks().forEach((track) => track.stop());
      setCameraOpen(true);

    } catch (error) {
      // Quando o usuário clica em "Bloquear"
      alert("Você precisa permitir o uso da câmera para tirar a foto.");
    }
  }

  async function handleScanReceipt(file) {
    const formData = new FormData();

    const fileToAppend = file ?? capturedFile;
    if (fileToAppend) {
      formData.append("file", fileToAppend);
    }

    const newReceipt = await scanReceipt(formData);
    if (!newReceipt?.data.totalValue || !newReceipt?.data.issueDate) {
      showToast("Nenhuma informação encontrada na foto.", "error");
      return
    }

    const fields = {
      issueDate: newReceipt.data.issueDate ?? null,
      emitterCNPJ: newReceipt.data.emitterCNPJ ?? null,
      emitterName: newReceipt.data.emitterName ?? null,
      totalValue: newReceipt.data.totalValue ?? null,
    };

    const hasNull = Object.values(fields).some((v) => !v);
    setFileName(file?.name ?? "");

    // Se faltar algo → abre modal manual
    if (hasNull) {
      setMissingFields(fields);
      setIsManualModalOpen(true);
    }
    setCameraOpen(false);
    setCapturedFile(file);
    setAdditionalInfo((prev) => ({
      ...prev,
      ...fields,
    }));
  }

  async function handleCreateReceipt(additionalInfo) {

    const newReceipt = await createReceipt({
      ...additionalInfo,
      category,
      description: noteInput,
    });

    if (!newReceipt?.data) {
      showToast(newReceipt?.message || "Nao foi possivel ciar a nota.", "error");
      return
    }

    setAdditionalInfo({
      issueDate: "",
      emitterCNPJ: "",
      emitterName: "",
      totalValue: null,
    });
    setNoteInput("");
    setBtnDisabled(true);
    setCapturedFile(null);
    setFileName("");
    setCameraOpen(false);
    setMissingFields({});
    const responseReceipts = await listReceipts();
    setReceipts(responseReceipts);
    setReceiptsFiltered(responseReceipts);

    showToast("Nota escaneada com sucesso!", "success");

  }

  // computed totals
  const totals = (() => {
    const gross = receipts.reduce((s, it) => s + Number(it.totalValue || 0), 0);
    const cashback = receipts.reduce(
      (s, it) => s + Number(it.cashbackReceived || 0),
      0
    )
    const byCat = {};
    receipts.forEach((it) => {
      byCat[it.category] = (byCat[it.category] || 0) + it.totalValue;
    });
    return { gross, cashback, byCat };
  })();

  // helpers
  const fmtBRL = (value) =>
    value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

  async function handleFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = null;

    setCapturedFile(null);
    handleScanReceipt(file);

    /*

    const forms = new FormData();
    forms.append("file", file);
    forms.append("category", category);
    forms.append("description", noteInput);

    const response = await uploadReceipt(forms);

    if (!response?.totalValue || !response?.emitter?.cnpj || !response?.issueDate) {
      showToast("Nao foi possivel escanear a nota.", "error");
      return
    }

    setFileName(file.name);

    setAdditionalInfo({
      issueDate: response.issueDate,
      emitterCNPJ: response.emitter.cnpj,
      emitterName: response.emitter.name,
      totalValue: response.totalValue,
    })
    */
  }

  return (
    <div className="p-4 max-w-6xl mx-auto var(--text-primary)">
      {/* header */}
      <div className="flex justify-between items-start mb-6">
        <div className="var(--text-primary)">
          {/* Botão de voltar discreto */}
          <button
            onClick={() => navigate("/pagina-principal")}
            className="flex items-center text-sm text-gray-400 hover:text-gray-200 transition mb-2"
          >
            ← Voltar
          </button>

          <h1 className="text-2xl font-semibold">X-Pay Cashback</h1>
          <p className="text-sm mt-1">
            Escaneie notas, escolha a categoria e ganhe cashback.
          </p>
        </div>

        <div>
          <button
            disabled={true}
            onClick={() => setRedeemOpen(true)}
            className="mt-8 px-4 py-2 bg-gray-600 text-gray-300 rounded-lg cursor-not-allowed"
          >
            Usar cashback
          </button>
        </div>
      </div>

      {/* KPIs */}
      <section className="grid gap-4 grid-cols-1 md:grid-cols-3 mb-6">
        <div className="bg-[var(--secondary-color)]/80 backdrop-blur-md rounded-2xl p-6 border border-[var(--primary-color)]">
          <div className="text-sm text-gray-500">Total escaneado</div>
          <div className="text-3xl font-bold mt-2">{fmtBRL(totals.gross)}</div>
        </div>

        <div className="bg-[var(--secondary-color)]/80 backdrop-blur-md rounded-2xl p-6 border border-[var(--primary-color)]">
          <div className="text-sm text-gray-500">Cashback acumulado</div>
          <div className="text-3xl font-bold mt-2">{fmtBRL(totals.cashback)}</div>
        </div>

        <div className="bg-[var(--secondary-color)]/80 backdrop-blur-md rounded-2xl p-6 border border-[var(--primary-color)]">
          <div className="text-sm text-gray-500">Bônus liberados</div>
          <div className="text-3xl font-bold mt-2">
            {Math.floor(totals.gross / 500)}x R$ 10
          </div>
        </div>
      </section>

      {/* Tabs */}
      <div className="mb-4">
        <div className="grid grid-cols-3 bg-[var(--secondary-color)]/80 backdrop-blur-md rounded-xl overflow-hidden border border-[var(--primary-color)]">

          <button
            onClick={() => setActiveTab("scan")}
            className={`
        py-2 px-1 font-semibold text-xs sm:text-sm md:text-base text-center
        ${activeTab === "scan" ? "bg-[var(--primary-color)] text-white" : "text-[var(--primary-color)]"}
      `}
          >
            Escanear
          </button>

          <button
            onClick={() => setActiveTab("history")}
            className={`
        py-2 px-1 font-semibold text-xs sm:text-sm md:text-base text-center
        ${activeTab === "history" ? "bg-[var(--primary-color)] text-white" : "text-[var(--primary-color)]"}
      `}
          >
            Minhas notas
          </button>

          <button
            onClick={() => setActiveTab("partners")}
            className={`
        py-2 px-1 font-semibold text-xs sm:text-sm md:text-base text-center
        ${activeTab === "partners" ? "bg-[var(--primary-color)] text-white" : "text-[var(--primary-color)]"}
      `}
          >
            Lojas parceiras
          </button>
        </div>
      </div>


      {/* Panels */}
      <div>
        {/* Scan Panel */}
        {activeTab === "scan" && (
          <div className="bg-[var(--secondary-color)]/80 backdrop-blur-md rounded-2xl p-6 border border-[var(--primary-color)]">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

              {/* Categoria */}
              <div>
                <label className="block text-sm mb-1">Categoria</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="bg-white text-black rounded-2xl p-1 border border-black w-full"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c.id} value={c.label}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Observações */}
              <div>
                <label className="block text-sm mb-1">Observações (opcional)</label>
                <input
                  value={noteInput}
                  style={{ color: "black" }}
                  onChange={(e) => setNoteInput(e.target.value)}
                  placeholder="Ex.: Posto Shell, cupom 123"
                  className="bg-white text-black rounded-2xl w-full p-2 border border-black w-full"
                />
              </div>

            </div>

            <div
              className="
    flex flex-col sm:flex-row
    items-stretch sm:items-center
    gap-3 mt-4
  "
            >
              {/* Botão Scan / Add */}
              <button
                disabled={btnDisabled}
                onClick={() => handleCreateReceipt(additionalInfo)}
                className={`
      w-full sm:w-auto
      text-center
      px-4 py-2 rounded-xl font-semibold
      break-words
      ${btnDisabled
                    ? "bg-gray-600 text-gray-200 cursor-not-allowed"
                    : "bg-[var(--primary-color)] text-white hover:bg-[var(--primary-color)]/80"
                  }
    `}
              >
                Escanear / Adicionar
              </button>

              {/* Botão Anexar */}
              <label
                className="
      w-full sm:w-auto
      text-center
      bg-[var(--secondary-color)]/80 backdrop-blur-md 
      rounded-2xl border border-[var(--primary-color)] 
      px-4 py-2 cursor-pointer
      break-words
    "
              >
                Anexar nota
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </label>

              {/* Botão Tirar Foto */}
              <label
                className="
      w-full sm:w-auto
      text-center
      bg-[var(--secondary-color)]/80 backdrop-blur-md 
      rounded-2xl border border-[var(--primary-color)] 
      px-4 py-2 cursor-pointer
      break-words
    "
              >
                Tirar foto
                <input
                  type="button"
                  onClick={() => handleOpenCamera()}
                  className="hidden"
                />
              </label>

              {/* Nome do arquivo */}
              <span className="text-sm text-gray-500 break-all mt-1 sm:mt-0">
                {fileName ? `Arquivo: ${fileName}` : ""}
              </span>
            </div>


            <div className="mt-6 p-4 border-dashed border rounded-lg border-gray-200">
              <div className="font-semibold mb-2">Como funciona</div>
              <ul className="text-sm text-gray-600 list-disc ml-5">
                <li>Para cada R$ 1,00 escaneado você ganha <b>R$ 0,05</b> de cashback.</li>
                <li>Escolha a categoria correta para estatísticas e benefícios.</li>
                <li>Você poderá usar o saldo em <b>lojas parceiras</b>.</li>
                <li>Notas falsas ou duplicadas serão removidas.</li>
              </ul>
            </div>
          </div>
        )}

        {/* History Panel */}
        {activeTab === "history" && (
          <div className="bg-[var(--secondary-color)]/80 backdrop-blur-md rounded-2xl p-6 border border-[var(--primary-color)] shadow-sm mb-6">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-2" id="filters">
                <FilterBadge
                  label="Todas"
                  active={activeFilter === "all"}
                  onClick={() => handleReceiptsByCategory("all")}
                />
                {CATEGORIES.map((c) => (
                  <FilterBadge
                    key={c.id}
                    label={c.label}
                    active={activeFilter === c.label}
                    onClick={() => handleReceiptsByCategory(c.label)}
                  />
                ))}
              </div>
            </div>

            {receiptsFiltered.length === 0 ? (
              <div className="text-sm text-gray-500">Nenhuma nota cadastrada ainda. Escaneie sua primeira nota na aba acima.</div>
            ) : (
              <div className="grid gap-3">
                {receiptsFiltered.map((it) => (
                  <div key={it.id} className=" bg-[var(--secondary-color)]/80 backdrop-blur-md rounded-2xl border border-[var(--primary-color)] p-4 border rounded-lg flex flex-col md:flex-row md:justify-between gap-3">
                    <div>
                      <div className="font-medium">{labelOf(it.category)}</div>
                      <div className="text-xs text-gray-500">{moment(it.issueDate).format("DD/MM/YYYY")}</div>
                      {it.status && <div className="text-sm text-gray-600 mt-2">{translateStatus(it.status)}</div>}
                    </div>
                    <div className="flex flex-col items-start md:items-end justify-center gap-2">
                      <div className="font-semibold">{formatCurrency(Number((it.totalValue)).toFixed(2))}</div>
                      <div className="text-sm text-gray-600">Cashback: <b>{formatCurrency(Number((it.cashbackReceived)).toFixed(2))}</b></div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Partners Panel */}
        {activeTab === "partners" && (
          <div className="bg-[var(--secondary-color)]/80 backdrop-blur-md rounded-2xl p-6 border border-[var(--primary-color)] shadow-sm mb-6">
            <div className="font-semibold mb-4">Lojas parceiras</div>

            {partners?.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {partners.map((p) => (
                  <div
                    className="p-4 border border-[var(--primary-color)] rounded-lg flex justify-between items-center"
                    key={p.id}
                  >
                    <div>
                      <div className="font-medium">{p.legalName}</div>

                      {p.partners.map((partner) => (
                        <div key={partner.id} className="text-sm text-gray-500">
                          {partner.name} — {partner.equityPercentage}%
                        </div>
                      ))}
                    </div>
                    <button
                      disabled
                      className="px-3 py-1 rounded-full text-sm bg-[var(--secondary-color)]/80 backdrop-blur-md border border-[var(--primary-color)] text-gray-700"
                    >
                      Ver ofertas
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-gray-500 italic">
                Lista de lojas parceiras vazia.

              </div>
            )}
          </div>
        )}
      </div>

      {/* Charts & rules section */}
      <section className="grid grid-cols-1 gap-4 mb-3 mt-4">
        <div className="bg-[var(--secondary-color)]/80 backdrop-blur-md rounded-2xl p-6 border border-[var(--primary-color)] shadow-sm">
          <div className="flex justify-between items-center mb-1">
            <div className="font-semibold">Total por categoria</div>
          </div>
          <PieCategoryChart
            receipts={receipts}
          />

          <div className="flex flex-wrap gap-2 mt-3">
            {groupByCategory(receipts).map((c) => (
              <span className="text-sm bg-[var(--secondary-color)]/80 backdrop-blur-md border border-[var(--primary-color)] px-3 py-1 rounded-full">
                {c.category}: {formatCurrency(Number(c.total).toFixed(2))}
              </span>
            ))}
          </div>
        </div>

        <div className="bg-[var(--secondary-color)]/80 backdrop-blur-md rounded-2xl p-6 border border-[var(--primary-color)] shadow-sm mb-2">
          <div className="font-semibold mb-3">Regras do programa</div>
          <ul className="text-sm text-gray-600 list-disc ml-5">
            <li>Cashback de <b>5%</b> sobre o valor total da nota fiscal escaneada.</li>
            <li>Saldo utilizável exclusivamente nas <b>lojas parceiras</b> credenciadas.</li>
            <li>Notas devem ser legíveis e referentes ao titular da conta.</li>
            <li>Auditoria automática remove duplicidades e notas inválidas.</li>
          </ul>
        </div>
      </section>

      <footer className="text-sm text-gray-500 flex justify-between">
        <div>2M Instituição de Pagamentos S/A • X-Pay Innovation Payments</div>
        <div>Cashback: R$ 0,05 por R$ 1,00 escaneado</div>
      </footer>

      <CameraModal isOpen={cameraOpen} onClose={() => setCameraOpen(false)} onSubmitPhoto={(file) => handleScanReceipt(file)} setCapturedFile={setCapturedFile} capturedFile={capturedFile} missingFields={missingFields} isManualModalOpen={isManualModalOpen} setIsManualModalOpen={setIsManualModalOpen} setAdditionalInfo={setAdditionalInfo} />

      {/* Redeem Modal */}
      {redeemOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <div className="font-bold text-lg">Usar cashback</div>
            <div className="text-sm text-gray-500 mt-1">Converta seu saldo em voucher para usar nas lojas parceiras.</div>
            <div className="mt-4">Saldo disponível: <b>{fmtBRL(totals.cashback)}</b></div>

            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setRedeemOpen(false)} className="px-3 py-1 border rounded-lg">Cancelar</button>
              <button
                onClick={() => {
                  if (totals.cashback < 10) {
                    alert("Valor mínimo para resgatar é R$ 10,00");
                    return;
                  }
                  const code = Math.random().toString(36).slice(2, 8).toUpperCase();
                  alert(`Voucher gerado: XP-${code}\nApresente o código na loja parceira.`);
                  setRedeemOpen(false);
                }}
                className="px-4 py-2 rounded-xl bg-[var(--primary-color)] text-white font-semibold"
              >
                Gerar voucher
              </button>
            </div>
          </div>
        </div>
      )}
      <ToastContainer />
    </div>
  );
}

/* small helper components */
function FilterBadge({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1 rounded-full text-sm ${active ? "bg-[var(--primary-color)] text-white" : "bg-[var(--secondary-color)]/80 backdrop-blur-md rounded-2xl p-6 border border-[var(--primary-color)] text-[var(--primary-color)]"}`}
    >
      {label}
    </button>
  );
}

function labelOf(id) {
  const c = CATEGORIES.find((x) => x.id === id);
  return c ? c.label : id;
}
